import { call, put, select } from 'redux-saga/effects'
import { selectExtraRoutesAuthData } from '@/containers/Login/AuthSelectors'
import { callApi } from '@/shared/tools/apiSagaHelper'
import { selectActiveAddress } from '@/containers/SubscriptionFormPageNew/SubscriptionForm/SubscriptionFormSelectors'
import { updateGasMeterAddressData } from '@/containers/SubscriptionFormPageNew/SubscriptionForm/SubscriptionFormSlice'
import { saveMeterShared } from '@/containers/SubscriptionFormPageNew/Components/shared/MeterSaveSharedSagas'
import * as Sentry from '@sentry/react'

export const gasMeterModuleActions = {
  METER_MODULE_REQUEST_PCE_DATA_V2: 'METER_MODULE_REQUEST_PCE_DATA_V2',
  METER_MODULE_VALIDATE_PCE_METER_SYNTAX_V2: 'METER_MODULE_VALIDATE_PCE_METER_SYNTAX_V2',
  SAVE_GAS_METER_V2: 'SAVE_GAS_METER_V2',
  PCE_SEARCH_V2: 'PCE_SEARCH_V2',
}

export function* requestPceMeterData({ body }) {
  const { podEligibilityUrl } = yield select(selectExtraRoutesAuthData)
  const activeAddress = yield select(selectActiveAddress)

  try {
    yield put(
      updateGasMeterAddressData({
        data: {
          pceEligibilityLoading: true,
          pceEligibilityData: {},
        },
        id: activeAddress,
      }),
    )
    yield call(callApi, {
      endpoint: podEligibilityUrl,
      showErrorSnackbar: false,
      payload: body,
      method: 'POST',
    })
  } catch (error) {
    const status = error?.status || 'unknown'
    if (import.meta.env.PROD && status !== 422) {
      Sentry.captureException(new Error('requestMeterDataError'), { extra: { error } })
    }
  }
}

export function* validatePceMeterSyntax({ meterId, callback }) {
  const { podValidateUrl } = yield select(selectExtraRoutesAuthData)
  try {
    const data = yield call(callApi, {
      endpoint: podValidateUrl,
      queryParams: { meterId, energyType: 'gas' },
      showErrorSnackbar: false,
    })
    yield call(callback, data)
  } catch (error) {
    const status = error?.status || 'unknown'
    if (import.meta.env.PROD && status !== 422) {
      Sentry.captureException(new Error('validateMeterSyntaxError'), { extra: { error } })
    }
  }
}

export function* saveGasMeter({ enqueue, meterData, addressId }) {
  return yield call(saveMeterShared, {
    enqueue,
    meterData,
    addressId,
    meterType: 'gas',
  })
}

export function* searchPce({ searchParams, onSuccess, onError, onBlocked }) {
  const { podSearchUrl } = yield select(selectExtraRoutesAuthData)

  try {
    const params = new URLSearchParams({
      lookup_street_name: searchParams.streetName || '',
      street_number: searchParams.streetNumber || '',
      zipcode: searchParams.zipcode || '',
      clientType: searchParams.contractType || 'part',
      pod_old_occupant: searchParams.occupant || '',
      matricule: searchParams.matricule || '',
      inseeCode: searchParams.inseeCode || '',
      apartment_complement: searchParams.complement || '',
      building: searchParams.building || '',
      locality: searchParams.locality || '',
      city: searchParams.city || '',
      serialNumber: searchParams.serialNumber || '',
      podType: 'PCE',
    })

    const fullUrl = `${podSearchUrl}?${params.toString()}`

    const response = yield call(callApi, {
      endpoint: fullUrl,
      showErrorSnackbar: false,
    })

    if (response.response) {
      yield call(onSuccess, response.response)
      return
    }

    if (response.error_message) {
      yield call(onError, response.error_message, response.error_code ?? null)
      return
    }

    if (response.id) {
      yield call(onSuccess, null, response.id)
    }
  } catch (error) {
    const blockCode = error?.responseData?.error?.code
    if (error?.status === 409 && typeof blockCode === 'string' && blockCode.startsWith('PCE_SEARCH_')) {
      yield call(onBlocked, blockCode, error.responseData.error.details?.retry_after ?? 0)
      return
    }

    const status = error?.status || 'unknown'
    if (import.meta.env.PROD && status !== 422) {
      Sentry.captureException(new Error('searchPceError'), { extra: { error } })
    }
    yield call(onError, 'An error occurred while searching for PCE data')
  }
}
